import type { Customer } from '@/types/nest/customer'
import { computed, ref, type Ref } from 'vue'
import isEmail from '@rearguard/is-email'

export type CustomerValidationScope = 'general' | 'billing'

export function useCustomerValidation(
  customer: Ref<Customer | null>,
  scope: CustomerValidationScope
) {
  const submitted = ref(false)

  const errors = computed(() => {
    const data = customer.value
    const result: Partial<Record<keyof Customer, string>> = {}

    if (!data) return result

    if (scope === 'general') {
      if (!data.name || data.name.trim().length === 0) {
        result.name = 'Name cannot be empty'
      }
      if (data.email && !isEmail(data.email)) {
        result.email = 'Invalid email address'
      }
    }

    if (scope === 'billing') {
      if (data.billingEmail && !isEmail(data.billingEmail)) {
        result.billingEmail = 'Invalid billing email address'
      }
    }

    return result
  })

  const visibleErrors = computed<Partial<Record<keyof Customer, string>>>(() =>
    submitted.value ? errors.value : {}
  )

  const isValid = computed(() => Object.keys(errors.value).length === 0)
  const isSubmittedAndInvalid = computed(() => submitted.value && !isValid.value)

  function validate(): boolean {
    submitted.value = true
    return isValid.value
  }

  function reset() {
    submitted.value = false
  }

  return { errors, visibleErrors, isValid, isSubmittedAndInvalid, validate, reset }
}
